/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisSidebarItem } from 'src/core/components/LuisSidebarItem';
import { useAnnounced } from 'src/core/hooks/useAnnounced';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getStylist, important } from 'src/core/themes/luisStylist';
import { LuisSidebarItemProps, LuisSidebarNormalItemProps } from 'src/core/types/luisSidebar';
import { coreComponentsTeachingBubbleMessages } from 'src/core/utils/coreComponentsTeachingBubbleMessages';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';

export type LuisSidebarProps = {
    /**
     * The items to display in the side-bar.
     */
    itemLists: LuisSidebarItemProps[][];

    /**
     * Optional param to define the toggle button's id.
     */
    sidebarToggleButtonId?: string;
};

const messages = defineMessages({
    sidebarExpanded: {
        id: 'portal.luisSidebar.sidebarExpanded',
        defaultMessage: 'Sidebar expanded'
    },
    sidebarCollapsed: {
        id: 'portal.luisSidebar.sidebarCollapsed',
        defaultMessage: 'Sidebar collapsed'
    },
    hamburgerIconCollapsed: {
        id: 'portal.luisSidebar.hamburgerIconCollapsed',
        defaultMessage: 'Hamburger menu icon: collapsed'
    },
    hamburgerIconExpanded: {
        id: 'portal.luisSidebar.hamburgerIconExpanded',
        defaultMessage: 'Hamburger menu icon: expanded'
    }
});

const { styleComponent } = getStylist('LuisSidebar');

const SidePanelRoot = styleComponent<IStackProps>(Stack)<{ isCollapsed: boolean }>(
    'SidePanelRoot',
    theme => ({ backgroundColor: theme.sidebarBackgroundColor, borderRight: theme.sidebarBorder, overflowY: 'auto' }),
    ({ customProps }, theme) => {
        const width: string = customProps.isCollapsed ? theme.sidebarWidthCollapsed : theme.sidebarWidthExpanded;

        return { minWidth: width, maxWidth: width };
    }
);

const StyledLuisSidebarItem = styleComponent<LuisSidebarItemProps & { className?: string }>(LuisSidebarItem)(
    'StyledLuisSidebarItem',
    {},
    ({ isCollapsed }, theme) => ({
        $nest: {
            '& [data-icon-container]': important({
                fontSize: theme.expandButtonFontSize,
                color: theme.expandButtonColor,
                ...(isCollapsed
                    ? {}
                    : {
                          position: 'absolute',
                          right: '6px',
                          top: '14px',
                          width: 'auto',
                          height: 'auto'
                      })
            })
        }
    })
);

/**
 * Renders a navigation sidebar. Used in the build and
 * manage panes in the LUIS application page.
 */
export const LuisSidebar = observer((props: LuisSidebarProps) => {
    const { itemLists, sidebarToggleButtonId: parentSidebarToggleButtonId } = props;
    const { uiStore, dispatcher } = useGlobalState();
    const { localize } = useLocalization();
    const { isXXLargeOrGreater } = useResponsiveMode();
    const { announceAction, announcedComponent } = useAnnounced();
    const sidebarToggleButtonId = React.useRef<string>(getId());
    const { isSidebarCollapsed } = uiStore;
    React.useEffect(() => () => dequeueTeachingBubble('LuisSidebarCollapse'), []);

    React.useEffect(() => {
        if (!isXXLargeOrGreater) {
            dispatcher.dispatch('setIsSidebarCollapsed', { isSidebarCollapsed: true });
        }
    }, [isXXLargeOrGreater]);

    const handleCollapseToggleClick = () => {
        if (!isSidebarCollapsed && !uiStore.teachingBubbles.LuisSidebarCollapse) {
            createTeachingBubble(
                'LuisSidebarCollapse',
                localize(coreComponentsTeachingBubbleMessages.sidebarCollapseBody),
                localize(coreComponentsTeachingBubbleMessages.sidebarCollapseTitle),
                `#${parentSidebarToggleButtonId ?? sidebarToggleButtonId.current}`,
                true
            );
        }
        announceAction(isSidebarCollapsed ? localize(messages.sidebarExpanded) : localize(messages.sidebarCollapsed));
        dispatcher.dispatch('toggleSidebar');
    };

    return (
        <SidePanelRoot customProps={{ isCollapsed: isSidebarCollapsed }} verticalFill role="list">
            <FocusZone>
                <div id={parentSidebarToggleButtonId ?? sidebarToggleButtonId.current}>
                    <StyledLuisSidebarItem
                        text=""
                        key="toggleCollapse"
                        isCollapsed={isSidebarCollapsed}
                        onClick={handleCollapseToggleClick}
                        iconName={isSidebarCollapsed ? 'DoubleChevronLeftMedMirrored' : 'DoubleChevronLeftMed'}
                        aria-label={
                            isSidebarCollapsed ? localize(messages.hamburgerIconCollapsed) : localize(messages.hamburgerIconExpanded)
                        }
                    />
                </div>

                {itemLists.map((itemList: LuisSidebarNormalItemProps[]) => (
                    <div key={getId()} role="tablist">
                        {itemList.map((item: LuisSidebarNormalItemProps, index: number) => (
                            <LuisSidebarItem key={item.text} {...item} isCollapsed={isSidebarCollapsed} index={index + 1} />
                        ))}
                    </div>
                ))}
            </FocusZone>
            {announcedComponent}
        </SidePanelRoot>
    );
});
